import { useCallback, useState } from 'react';
import { FileText } from 'lucide-react';
import { updateTranscriptsFeature } from '../../api';
import { useFeatures } from '../../contexts/FeaturesContext';
import { useToast } from '../../hooks/use-toast';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';

const describeError = (error) =>
  error?.response?.data?.message || error?.message || 'Неизвестная ошибка';

/**
 * Тумблер отображения расшифровок. Управляет только показом: распознавание речи продолжает
 * работать, файл расшифровки остаётся в S3, а анализ по-прежнему идёт по тому же тексту.
 */
const TranscriptVisibilityCard = ({ user }) => {
  const { toast } = useToast();
  const { features, isLoadingFeatures, refreshFeatures } = useFeatures();
  const isAdmin = user?.role === 'admin';
  const [isSaving, setIsSaving] = useState(false);

  const handleToggle = useCallback(
    async (visible) => {
      setIsSaving(true);
      try {
        await updateTranscriptsFeature(visible);
        await refreshFeatures();
        toast({
          title: visible ? 'Расшифровки показываются' : 'Расшифровки скрыты'
        });
      } catch (error) {
        toast({
          title: 'Не удалось изменить настройку',
          description: describeError(error),
          variant: 'destructive'
        });
      } finally {
        setIsSaving(false);
      }
    },
    [refreshFeatures, toast]
  );

  if (!isAdmin) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Расшифровки звонков
            <Badge variant={features.transcriptsVisible ? 'default' : 'outline'}>
              {features.transcriptsVisible ? 'Показываются' : 'Скрыты'}
            </Badge>
          </CardTitle>
          <CardDescription>
            Показывать ли текст диалога в интерфейсе. Настройка общая для всей инсталляции,
            а не персональная.
          </CardDescription>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id="transcript-visibility"
            data-ev="settings.transcripts.visibility_toggle"
            checked={features.transcriptsVisible}
            disabled={isSaving || isLoadingFeatures}
            onCheckedChange={handleToggle}
          />
          <Label htmlFor="transcript-visibility">Показывать</Label>
        </div>
      </CardHeader>

      <CardContent className="space-y-2 text-sm text-muted-foreground">
        <p>
          При выключении из интерфейса пропадают: блок «Расшифровка звонка» в карточке звонка,
          транскрипция в карточке клиента и в «Пропущенных», кнопка «Транскрипт» на вкладке
          «Задания». Расшифровки перестают попадать и в архив заданий, а сервер больше не отдаёт
          текст в API.
        </p>
        <p>
          Распознавание речи при этом работает как работало: звонки расшифровываются, текст
          сохраняется и анализируется. Аудиозапись остаётся доступной везде, поиск по звонкам
          продолжает искать по содержанию разговора, а график «Запрещённые фразы» продолжает
          считаться — он возвращает числа, а не текст. Данные не удаляются: включение вернёт
          всё на место.
        </p>
      </CardContent>
    </Card>
  );
};

export default TranscriptVisibilityCard;
